/* eslint-disable import/prefer-default-export */
import timezone from "dayjs/plugin/timezone";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  AmountAPISymbol,
  AmountHiddenSymbol,
  AmountSymbol,
  StockLargeClassification,
  Supplier,
} from "../types";
import { amountSymbols } from "../constants";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Tokyo");

/**
 * 記号表示を計算
 * @param amount 数量
 * @param thresholdList 在庫数を分ける数量の閾値
 */
export const calcSymbol = (amount: number, thresholdList: number[]) => {
  thresholdList.sort((a, b) => b - a); // 降順
  let aSymbol: AmountSymbol = "×";
  let index = -1;
  for (let i = 0; i < thresholdList.length; i += 1) {
    const th = thresholdList[i];
    if (th <= amount) {
      index = i;
      break;
    }
  }
  if (index >= 0 && amountSymbols.length - 1 >= index) {
    aSymbol = amountSymbols[index];
  }
  return aSymbol;
};

/**
 * 設定値が存在しているか
 * @param thresholdList 最大、最小の設定値が入っている配列
 */
export const checkSymbolThresholdExist = (thresholdList?: number[]) =>
  thresholdList && thresholdList.length >= 2;

/**
 * 表示する在庫数量を計算
 * @param amount 在庫数
 * @param sVisibleType 在庫数量の表示方法
 * @param thresholdList 最大、最小の設定値が入っている配列
 */
export const dispSymbol = (
  amount: number,
  sVisibleType: number | null | undefined,
  thresholdList?: number[],
) => {
  if (sVisibleType === 0) {
    return amount;
  }
  if (sVisibleType === 2 || sVisibleType == null) {
    // 在庫数表示が在庫非表示のとき
    return "-";
  }
  if (!checkSymbolThresholdExist(thresholdList)) {
    // 在庫数設定値がない場合は見られたくないケースもあるため非表示
    // return amount;
    return "-";
  }
  return calcSymbol(amount, thresholdList!);
};

/**
 * 半角カタカナを全角カタカナにする
 * @param str 文字列
 */
export const narrowToWideKana = (str: string): string => {
  const convertMap = {
    ｧ: "ァ",
    ｨ: "ィ",
    ｩ: "ゥ",
    ｪ: "ェ",
    ｫ: "ォ",
    ｬ: "ャ",
    ｭ: "ュ",
    ｮ: "ョ",
    ｯ: "ッ",
    ｰ: "ー",
    ｱ: "ア",
    ｲ: "イ",
    ｳ: "ウ",
    ｴ: "エ",
    ｵ: "オ",
    ｶ: "カ",
    ｷ: "キ",
    ｸ: "ク",
    ｹ: "ケ",
    ｺ: "コ",
    ｻ: "サ",
    ｼ: "シ",
    ｽ: "ス",
    ｾ: "セ",
    ｿ: "ソ",
    ﾀ: "タ",
    ﾁ: "チ",
    ﾂ: "ツ",
    ﾃ: "テ",
    ﾄ: "ト",
    ﾅ: "ナ",
    ﾆ: "ニ",
    ﾇ: "ヌ",
    ﾈ: "ネ",
    ﾉ: "ノ",
    ﾊ: "ハ",
    ﾋ: "ヒ",
    ﾌ: "フ",
    ﾍ: "ヘ",
    ﾎ: "ホ",
    ﾏ: "マ",
    ﾐ: "ミ",
    ﾑ: "ム",
    ﾒ: "メ",
    ﾓ: "モ",
    ﾔ: "ヤ",
    ﾕ: "ユ",
    ﾖ: "ヨ",
    ﾗ: "ラ",
    ﾘ: "リ",
    ﾙ: "ル",
    ﾚ: "レ",
    ﾛ: "ロ",
    ﾜ: "ワ",
    ﾝ: "ン",
    ｦ: "ヲ",
    ｶﾞ: "ガ",
    ｷﾞ: "ギ",
    ｸﾞ: "グ",
    ｹﾞ: "ゲ",
    ｺﾞ: "ゴ",
    ｻﾞ: "ザ",
    ｼﾞ: "ジ",
    ｽﾞ: "ズ",
    ｾﾞ: "ゼ",
    ｿﾞ: "ゾ",
    ﾀﾞ: "ダ",
    ﾁﾞ: "ヂ",
    ﾂﾞ: "ヅ",
    ﾃﾞ: "デ",
    ﾄﾞ: "ド",
    ﾊﾞ: "バ",
    ﾋﾞ: "ビ",
    ﾌﾞ: "ブ",
    ﾍﾞ: "ベ",
    ﾎﾞ: "ボ",
    ﾊﾟ: "パ",
    ﾋﾟ: "ピ",
    ﾌﾟ: "プ",
    ﾍﾟ: "ペ",
    ﾎﾟ: "ポ",
  };

  // eslint-disable-next-line no-restricted-syntax
  for (const [narrowKana, wideKana] of Object.entries(convertMap)) {
    // eslint-disable-next-line no-param-reassign
    str = str.replace(new RegExp(narrowKana, "g"), wideKana);
  }
  return str;
};

const toHalfWidth = (str: string) => {
  // 全角英数字を半角に変換
  // eslint-disable-next-line no-param-reassign
  str = str.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0));
  return str;
};

/**
 * カタカナをひらがなに変換
 * @param str 文字列
 */
export const toHiragana = (str: string | null | undefined) => {
  if (str === null || str === undefined) return "";
  return narrowToWideKana(toHalfWidth(str.toLowerCase()))
    .normalize("NFKC")
    .replace(/[\u30a1-\u30f6]/g, (match) => {
      const char = match.charCodeAt(0) - 0x60;
      return String.fromCharCode(char);
    });
};

/**
 * ダウンロードURL
 * @param url リンクのURL
 * @param prefix fileの最初に入れる文字列
 */
export const downloadFromUrl = async (url: string, prefix: string) => {
  const link = document.createElement("a");
  const date = new Date();
  link.href = url;
  link.download = `${prefix}${date.toISOString().split(".")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * 仕入先を重複無しで抽出する。
 * @param classificationList 大分類のリスト
 */
export const  extractNoDupplicationSupplier = (classificationList: StockLargeClassification[]) => {
  const sCdList: string[] = [];
  const sList: Supplier[] = [];
  classificationList.forEach((c) => {
    if (c.suppliers) {
      c.suppliers.forEach((s) => {
        if (!sCdList.includes(s.supplierCd)) {
          sList.push(s);
          sCdList.push(s.supplierCd);
        }
      });
    }
  });
  return sList;
};

/**
 * ISO8601を日本時間YYYY-MM-DD HH:mmに変換する
 * @param str ISO文字列
 */
export const convertUTCToDateTimeMinutes = (str: string | null | undefined) =>
  // const dateTimeArray = str.split('T');
  // const dateStr = dateTimeArray[0].split('-').join('/');
  // const dateTimeStr = dateTimeArray[1].split(':').slice(0,2).join(':');
  // return `${dateStr} ${dateTimeStr}`;
  str ? dayjs(str).tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm") : "";

/**
 * ISO8601を日本時間のYYYY/MM/DDに変換する
 * @param str ISO文字列
 */
export const convertUTCToDate = (str: string | null | undefined) =>
  str ? dayjs(str).tz("Asia/Tokyo").format("YYYY-MM-DD") : "";

/**
 *
 * @param apiSymbol
 */
export const convertSymbolAPItoClient = (apiSymbol: AmountAPISymbol) => {
  const map: { [Key in AmountAPISymbol]: AmountHiddenSymbol } = {
    〇: "○",
    "△": "△",
    "✖": "×",
    "-": "-",
  };
  return map[apiSymbol];
};

/**
 * ゼロを区別するlocaleString
 * @param val 変換する値
 */
export const localeStringDisZero = (val: number | string | undefined | null) => {
  if (typeof val === "number") return val.toLocaleString();
  if (typeof val === "string") return val;
  return "-";
};

/**
 * 仕入先の日時フィールドを在庫表示設定により表示するかを制御
 * @param sDate 仕入先の更新日時
 * @param sAmount 仕入先の在庫数
 */
export const checkDisplaySupplier = (
  sDate: string | undefined,
  sAmount: string | number | undefined,
) => {
  if (sAmount === "-") return "-";
  if (!sDate) return "-";
  return sDate;
};

/**
 * nullやundefinedを空文字に変える
 * @param val 対象の引数
 * @param defaultVal null, undefinedだった場合のデフォルトの値
 */
export const nullToBlank = (val: number | string | null | undefined, defaultVal?: string) =>
  val == null ? (defaultVal ?? "") : val;

/**
 * nullやundefinedを0に変える
 * @param val 対象の引数
 */
export const nullToZero = (val: number | null | undefined): number => val == null ? 0 : val;

/**
 * 空の場合にapiに空の情報を送るのを防ぐため、空のkey,valueは削除しておくための関数
 * @param obj 空のkey, valueを取り除くオブジェクト
 * @param requiredKeys 必須のキー
 */
export const removeEmptyValues = <T extends Record<string, any>, K extends keyof T>(
  obj: T,
  requiredKeys: readonly K[] = []
): Pick<T, K> & Partial<Omit<T, K>> => Object.entries(obj).reduce<Pick<T, K> & Partial<Omit<T, K>>>((acc, [key, value]) => {
  const isEmpty =
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '');

  if (!isEmpty || (requiredKeys as readonly (keyof T)[]).includes(key)) {
    (acc as any)[key as keyof T] = value;
  }
  return acc;
}, {} as any);

/**
 * 小配列ずつに分割する
 * @param arr 配列
 * @param size いくつずつ分けるか
 * @param offset いくつ進んだところからスタートするか
 */
export const chunkArray = <T>(arr: T[], size: number, offset: number = 0): T[][] => {
  const result: T[][] = [];
  for (let i = offset; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size))
  }
  return result;
};
